/* eslint-disable react/display-name, react-hooks/exhaustive-deps */
import React, { createContext, useState, useEffect } from "react";

import isEqual from "lodash/isEqual";

// import utils
import getDataForChannel from "../utils/env/getDataForChannel";
// import getRegistrationLockedStatus from '@utils/embeds/registration/getRegistrationLockedStatus'

import eventTracking, {
  generateTrackingValue,
  tealium,
} from "../utils/analytics/index";
import enterViewport from "../utils/dom/enterViewport";
import VideoSequence from "../components/VideoSequence";
import AndroidVideoSequence from "../components/VideoSequence/Android";

import videoData from '../components/VideoSequence/data.json';

// Create Context
export const EnvironmentContext = createContext({});

// Create context Provider
export const EnvironmentProvider = ({
  initialProps,
  data,
  theme,
  envRefreshTrigger,
}) => {
  // create state items
  const [device, setDevice] = useState(null);
  const [envData, setEnvData] = useState(getDataForChannel(initialProps));
  const [retryCount, setRetryCount] = useState(0);
  const [enteredViewport, setEnteredViewport] = useState(false);

  const isAndroidDevice = (
    navigator?.userAgent?.match(/Android/i) ||
    navigator?.userAgent?.match(/SamsungBrowser/i) ||
      (window?.DNA_APP_ENV?.platform &&
        window.DNA_APP_ENV.platform == "android")
  );

  // function to re-get environment data.
  function _getEnvironmentContextData() {
    const newEnvData = getDataForChannel(initialProps);
    if (!isEqual(envData, newEnvData)) {
      console.log(
        "_getEnvironmentContextData: envData and newEnvData not equal - update state",
        envData,
        newEnvData
      );
      setEnvData(newEnvData);
    } else {
      console.log(
        "_getEnvironmentContextData: envData and newEnvData equal - do nothing",
        envData
      );
    }
    validateEnvData(newEnvData);
  }

  // Function to validate Environment Data and to schedule a retry if needed.
  function validateEnvData(newEnvData) {
    const { site, userId, userAccessLevel } = newEnvData || {};
    const isValid = site && userId && userAccessLevel;

    if (envData.isApp || window.location.search.match("debugEditorialTools")) {
      console.log(
        "validateEnvData",
        newEnvData,
        data.type,
        data.id,
        initialProps.isPreview,
        isValid,
        site,
        userId,
        userAccessLevel,
        isValid
      );
    }

    // if its valid, reset count and return true
    if (isValid) {
      if (envData.isApp) {
        // it is hard to debug env data on webviews as by the time u connect with devtools page is already loaded.
        // setting a timeout so that we can periodically see the envData in a webview.
        const time = retryCount * 500 + 2000;
        setTimeout(() => setRetryCount(retryCount + 1), time);
      } else {
        if (retryCount > 0) setRetryCount(0);
      }

      return true;
    }
    // if its preview dont schedule any retries coz it'll never exist.
    // if (initialProps.isPreview) return false;

    // schedule a retry if below threshold.
    if (retryCount < 5) {
      const time = retryCount * 500 + 500;
      console.log(
        "no site, userid or access data in env Data. lets set a timeout to check again. retries: ",
        retryCount,
        time
      );
      setTimeout(() => {
        console.log("timeout", retryCount, retryCount + 1);
        setRetryCount(retryCount + 1);
      }, time);
    } else {
      console.log("hit the retry limit for fetching env data.", newEnvData);
    }

    return false;
  }

  // side effect for componentDidMount
  useEffect(() => {
    // if its not web, load tealium
    if (envData.loadTealium) tealium(envData);

    // setup viewport helper for default tracking.
    const initDefaultViewportTracking = initialProps.rootEl;

    enterViewport(data.type, initialProps.rootEl, () => {
      setEnteredViewport(true);
      // console.log('initial enter viewport', data.type)

      if (initDefaultViewportTracking) {
        eventTracking(
          {
            eVar25: generateTrackingValue(
              data,
              `${videoData.analyticsKey}-interactive|viewport`
            ),
            events: ["event158"],
          },
          envData
        );
      }
    });
  }, [initialProps]);

  // side effect to retry _getEnvironmentContextData on change of retryCount
  useEffect(() => {
    // console.log("retryCount", retryCount);
    if (retryCount) _getEnvironmentContextData();
  }, [retryCount]);

  // create context provider value object container all states / handlers
  const value = {
    ...envData,
    theme,
    device,
    enteredViewport,
    // methods
    _getEnvironmentContextData,
  };

  // console.log("EnvironmentProvider", theme, value);

  return (
    <EnvironmentContext.Provider value={value}>
      {isAndroidDevice ? (<AndroidVideoSequence idPrefix={videoData?.idPrefix || "video3"}/>):(<VideoSequence idPrefix={videoData?.idPrefix || "video3"}/>)}
    </EnvironmentContext.Provider>
  );
};

export default EnvironmentProvider;

// export context helper hoc
export const withEnvironmentContext = (Component) => (props) =>
  (
    <EnvironmentContext.Consumer>
      {(envData) => (
        <Component
          {...props}
          envData={envData}
          device={envData.device || undefined}
        />
      )}
    </EnvironmentContext.Consumer>
  );
